import { useParams, Link } from 'react-router-dom'

// Mock data para demonstração
const processData = {
  '1': {
    name: 'Auditoria Interna Q1',
    description: 'Processo de auditoria interna do primeiro trimestre de 2025',
    status: 'Em andamento',
    areaId: 'compliance',
    areaName: 'Compliance',
    createdAt: '2025-01-15',
    lastUpdate: '2025-01-20',
    videoUrl: 'https://example.com/video1.mp4',
    hasVideo: true
  },
  '4': {
    name: 'Pipeline ETL Vendas',
    description: 'Desenvolvimento do pipeline de extração, transformação e carregamento de dados de vendas',
    status: 'Em andamento',
    areaId: 'engenharia-dados',
    areaName: 'Eng. de Dados',
    createdAt: '2025-01-10',
    lastUpdate: '2025-01-22',
    videoUrl: null,
    hasVideo: false
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Em andamento': return 'bg-blue-100 text-blue-800'
    case 'Concluído': return 'bg-green-100 text-green-800'
    case 'Pendente': return 'bg-yellow-100 text-yellow-800'
    case 'Planejamento': return 'bg-purple-100 text-purple-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

export default function ProcessPage() {
  const { areaId, processId } = useParams<{ areaId: string; processId: string }>()
  
  if (!processId || !processData[processId as keyof typeof processData]) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Processo não encontrado</h1>
          <Link to="/" className="btn-primary">Voltar ao início</Link>
        </div>
      </div>
    )
  }

  const process = processData[processId as keyof typeof processData]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700">Início</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/areas/${areaId}`} className="text-gray-500 hover:text-gray-700">{process.areaName}</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{process.name}</span>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{process.name}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(process.status)}`}>
                  {process.status}
                </span>
              </div>
              <p className="text-gray-600 mb-4">{process.description}</p>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span>Área: {process.areaName}</span>
                <span>Criado em: {new Date(process.createdAt).toLocaleDateString('pt-BR')}</span>
                <span>Última atualização: {new Date(process.lastUpdate).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
                Editar
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Atualizar Status
              </button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Section */}
            {process.hasVideo ? (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Vídeo do Processo</h2>
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <div className="flex flex-col items-center">
                    <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h2m3 0h2m3-12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600 mb-4">Vídeo salvo com sucesso!</p>
                    <button className="btn-primary">
                      ▶ Reproduzir Vídeo
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Vídeo do Processo</h2>
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 mb-4">Nenhum vídeo foi anexado a este processo</p>
                  <button className="btn-secondary">
                    Adicionar Vídeo
                  </button>
                </div>
              </div>
            )}

            {/* Process Details */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Detalhes do Processo</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {process.description}
                </p>
                <div className="mt-6 space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Objetivo</h3>
                    <p className="text-gray-600">Documentar e padronizar os procedimentos relacionados ao processo.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Escopo</h3>
                    <p className="text-gray-600">Este processo abrange todas as atividades e etapas necessárias para a execução eficiente.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Process Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(process.status)}`}>
                    {process.status}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Área</dt>
                  <dd className="text-sm text-gray-900">{process.areaName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Criado em</dt>
                  <dd className="text-sm text-gray-900">{new Date(process.createdAt).toLocaleDateString('pt-BR')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Última atualização</dt>
                  <dd className="text-sm text-gray-900">{new Date(process.lastUpdate).toLocaleDateString('pt-BR')}</dd>
                </div>
              </dl>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
              <div className="space-y-3">
                <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  📝 Editar processo
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  📹 Gerenciar vídeo
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                  📊 Visualizar relatórios
                </button>
                <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                  🗑️ Excluir processo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
